/**
 * Main Entry Point for ASD ICP v4 TypeScript Application
 * This file imports and initializes all application modules
 */

// Core modules
import { CanisterClient } from './canister-client';
import { ConfigManager } from './config-manager';
// ic-agent.ts is deprecated — all canister calls now use AgentAdapter via communication/index.ts

// UI modules
import { PageManager } from './ui/page-management';
import { ToastNotifications } from './ui/toast-notifications';
import { TimezoneSettings } from './ui/timezone-settings';

// Discovery modules
import { DCMSDiscovery } from './discovery/dcms-discovery';
import { DiscoveryLogger } from './discovery/discovery-logging';

// AI Assistant
import { AIAssistant } from './ai/ai-assistant';

// Auth
import { AuthManager } from './auth/auth-manager';

// Internet Identity
import { IIAgent } from './ii-agent/ii-agent';
import { IIAgentUI } from './ii-agent/ii-agent-ui';

// Service Mapper
import { ServiceMapController } from './service-mapper/service-map-controller';
import { ServiceMapVisualizer } from './service-mapper/service-map-visualizer';
import { ServiceMapperClient } from './service-mapper/service-mapper';

// Vector Search
import { VectorSearch } from './vector/vector-search';

// Knowledge Base
import { KnowledgeTab } from './knowledge/knowledge-tab';

// Services
import { unifiedAdaContext, UnifiedAdaContextService } from './services/unified-ada-context';
import { adaAgenticChat, AdaToolExecutor } from './services/ada-tools';

// Header Ask Ada (unified across all pages)
import { headerAskAda, HeaderAskAdaController } from './ui/controllers/header-ask-ada-controller';

// LLM Switcher (Sprint 59)
import { llmSwitcher, LLMSwitcherController } from './ui/components/llm-switcher';

// User Admin (Sprint 44)
import { UserAdminController } from './ui/controllers/user-admin-controller';

// Utils
import { FilterService } from './utils/filter-service';
import { PaginationService } from './utils/pagination-service';
import { SettingsService } from './utils/settings-service';
import { TableSorter } from './utils/table-sorter';
import { FeatherHelper } from './utils/feather-helper';

// App Core
import { AppCore } from './core/app-core';

// Window Functions (extracted from inline JavaScript - Sprint 12)
import { initializeWindowFunctions } from './core/window-functions';

// Component Loader (Sprint 12: Dynamic component loading)
import { componentLoader } from './components/component-loader';

/**
 * Global Application Interface
 * Makes all modules available on window object for compatibility
 */
declare global {
    interface Window {
        // Core
        canisterClient: CanisterClient;
        configManager: ConfigManager;
        /** @deprecated Use callCanister from api/communication instead */
        icAgent: any;
        appCore: AppCore;

        // UI
        pageManagement: PageManager;
        toastNotifications: ToastNotifications;
        timezoneSettings?: TimezoneSettings;

        // Discovery
        DCMSDiscovery: typeof DCMSDiscovery;
        dcmsDiscovery: DCMSDiscovery;
        DiscoveryLogger: typeof DiscoveryLogger;
        discoveryLogger: DiscoveryLogger;

        // AI
        aiAssistant: AIAssistant;

        // Auth
        authManager: AuthManager;

        // Internet Identity
        IIAgent: typeof IIAgent;
        iiAgent: IIAgent;
        IIAgentUI: typeof IIAgentUI;

        // Service Mapper
        serviceMapController: ServiceMapController;
        serviceMapperClient: ServiceMapperClient;
        ServiceMapVisualizer: typeof ServiceMapVisualizer;
        ServiceMapperClient: typeof ServiceMapperClient;

        // Vector
        vectorSearch: VectorSearch;

        // Knowledge Base
        knowledgeTab: KnowledgeTab;

        // Utils
        FilterService: typeof FilterService;
        PaginationService: typeof PaginationService;
        SettingsService: typeof SettingsService;
        TableSorter: typeof TableSorter;

        // LLM Switcher
        llmSwitcher: LLMSwitcherController;

        // Classes
        CanisterClient: typeof CanisterClient;
        ConfigManager: typeof ConfigManager;
        PageManager: typeof PageManager;
        ToastNotifications: typeof ToastNotifications;
        AIAssistant: typeof AIAssistant;
        AuthManager: typeof AuthManager;
        ServiceMapController: typeof ServiceMapController;
        VectorSearch: typeof VectorSearch;
        AppCore: typeof AppCore;
    }
}

/**
 * Initialize Application
 * Creates global instances and sets up the application
 */
function initializeApplication(): void {
    console.log('🚀 Initializing ASD ICP v4 Application (TypeScript)');

    // Initialize window functions first (required for onclick handlers)
    initializeWindowFunctions();

    // Create global instances
    window.canisterClient = new CanisterClient();
    window.configManager = new ConfigManager();
    // window.icAgent deprecated — kept as null for backward compat
    window.icAgent = null;
    window.pageManagement = new PageManager();
    window.toastNotifications = new ToastNotifications();
    window.dcmsDiscovery = new DCMSDiscovery();
    window.discoveryLogger = new DiscoveryLogger();
    window.aiAssistant = new AIAssistant();
    window.authManager = new AuthManager();
    window.iiAgent = new IIAgent();
    window.serviceMapController = new ServiceMapController(window.iiAgent);
    // NOTE: Don't initialize visualizer here - wait until modal is shown (container is visible)
    window.serviceMapperClient = new ServiceMapperClient();
    window.vectorSearch = new VectorSearch();
    window.knowledgeTab = new KnowledgeTab(window.canisterClient);
    window.llmSwitcher = llmSwitcher;
    window.appCore = new AppCore();

    // Export classes for instantiation
    window.CanisterClient = CanisterClient;
    window.ConfigManager = ConfigManager;
    window.DCMSDiscovery = DCMSDiscovery;
    window.DiscoveryLogger = DiscoveryLogger;
    window.PageManager = PageManager;
    window.ToastNotifications = ToastNotifications;
    window.AIAssistant = AIAssistant;
    window.AuthManager = AuthManager;
    window.IIAgent = IIAgent;
    window.IIAgentUI = IIAgentUI;
    window.ServiceMapController = ServiceMapController;
    window.ServiceMapVisualizer = ServiceMapVisualizer;
    window.ServiceMapperClient = ServiceMapperClient;
    window.VectorSearch = VectorSearch;
    window.FilterService = FilterService;
    window.PaginationService = PaginationService;
    window.SettingsService = SettingsService;
    window.TableSorter = TableSorter;
    window.AppCore = AppCore;

    // Initialize Feather icons replacement (inline SVG)
    console.log('🎨 Initializing Feather icons (inline SVG)...');
    FeatherHelper.replace();

    // Initialize Header Ask Ada (unified Ask Ada across all pages)
    console.log('🤖 Initializing Header Ask Ada...');
    headerAskAda.initialize();

    // Check Claude API key status on startup
    console.log('🔑 Checking Claude API key status...');
    checkClaudeApiKeyOnStartup();

    console.log('✅ Application initialized successfully');
    console.log('📦 Available modules:', Object.keys(window).filter(k =>
        ['canisterClient', 'configManager', 'pageManagement', 'toastNotifications',
         'aiAssistant', 'authManager', 'vectorSearch', 'appCore'].includes(k)
    ));

    // Check if using dynamic shell (Sprint 12)
    loadComponentsIfNeeded();
}

/**
 * Load HTML components dynamically if using shell approach
 * Sprint 12: Supports both build-time concatenation and dynamic loading
 */
async function loadComponentsIfNeeded(): Promise<void> {
    // Check if dynamic loading containers exist (shell approach)
    const startupContainer = document.getElementById('startup-container');
    const pagesContainer = document.getElementById('pages-container');
    const modalsContainer = document.getElementById('modals-container');

    // If containers don't exist or already have content, skip dynamic loading
    if (!startupContainer || !pagesContainer || !modalsContainer) {
        console.log('📄 Using build-time HTML (components pre-assembled)');
        return;
    }

    if (startupContainer.innerHTML.trim() !== '') {
        console.log('📄 Components already loaded');
        return;
    }

    console.log('🔄 Loading HTML components dynamically...');

    try {
        // Load layout components
        await Promise.all([
            componentLoader.loadLayout('startup', 'startup-container'),
            componentLoader.loadLayout('sidebar', 'sidebar-container'),
            componentLoader.loadLayout('header', 'header-container')
        ]);
        console.log('✅ Layout components loaded');

        // Load all page components into pages-container
        const pages = [
            'dashboard', 'devices', 'discovery', 'applications',
            'relationships', 'vector-search', 'credentials',
            'service-models', 'knowledge-base', 'settings', 'ask-ada'
        ];

        // Load pages sequentially to maintain order
        for (const page of pages) {
            const html = await fetch(`components/pages/${page}.html`).then(r => r.text());
            pagesContainer.innerHTML += html;
        }
        console.log('✅ Page components loaded');

        // Load all modal components into modals-container
        const modals = [
            'service-map', 'cmdb', 'device-table', 'add-credential',
            'ai-assistant', 'schedule-discovery', 'device-details',
            'login', 'service-log'
        ];

        for (const modal of modals) {
            const html = await fetch(`components/modals/${modal}.html`).then(r => r.text());
            modalsContainer.innerHTML += html;
        }
        console.log('✅ Modal components loaded');

        // Re-initialize Feather icons for dynamically loaded content
        FeatherHelper.replace();
        console.log('✅ All components loaded successfully');

    } catch (error) {
        console.error('❌ Failed to load components:', error);
    }
}

/**
 * Check Claude API key status on startup
 * Verifies the API key is configured and shows a warning if not
 */
async function checkClaudeApiKeyOnStartup(): Promise<void> {
    try {
        const canisterClient = window.canisterClient;
        if (!canisterClient) {
            console.warn('🔑 CanisterClient not available for API key check');
            return;
        }

        const isConfigured = await canisterClient.isClaudeConfigured();

        if (isConfigured) {
            console.log('✅ Claude API key is configured');
        } else {
            console.warn('⚠️ Claude API key is NOT configured');
            // Show a toast notification to alert the user
            if (window.toastNotifications) {
                window.toastNotifications.show(
                    'Claude API key not configured. Go to Settings to add your API key.',
                    'warning',
                    8000
                );
            }
        }
    } catch (error) {
        console.error('🔑 Failed to check Claude API key:', error);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApplication);
} else {
    // DOM already loaded
    initializeApplication();
}

// Export for module usage
export {
    CanisterClient,
    ConfigManager,
    PageManager,
    ToastNotifications,
    TimezoneSettings,
    DCMSDiscovery,
    DiscoveryLogger,
    AIAssistant,
    AuthManager,
    IIAgent,
    IIAgentUI,
    ServiceMapController,
    ServiceMapVisualizer,
    ServiceMapperClient,
    VectorSearch,
    KnowledgeTab,
    FilterService,
    PaginationService,
    SettingsService,
    TableSorter,
    AppCore,
    componentLoader,
    unifiedAdaContext,
    UnifiedAdaContextService,
    headerAskAda,
    HeaderAskAdaController,
    UserAdminController,
    llmSwitcher,
    LLMSwitcherController
};
